import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { LoadingPanel } from '@/components/px/LoadingPanel'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import {
  fetchProjects,
  selectProjectsCache,
  selectProjectsError,
  selectProjectsItems,
} from '@/features/projects/slices/projectsSlice'
import { shouldFetch } from '@/store/fetchCache'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * `/project` with no id — resolve the caller's FIRST live engagement and land
 * there; land on the portfolio when they have none.
 *
 * PX-W6.6 (spec §6.3 item 2) replaced the route table's
 * `<Navigate to="/project/…">` on a hard-coded demo slug: a fixture id in a
 * product route is a 404 for every real tenant, and it made the shortest path
 * into the workspace lead somewhere that does not exist.
 *
 * ⚠ THE DECISION WAITS FOR THE FETCH TO SETTLE, and "settled" is not `!loading`
 * (merged review C3). `loading` is false on the FIRST render — the dispatch
 * lives in an effect, which runs after that render commits — so a `!loading`
 * check saw an empty list and redirected to `/projects` before the request had
 * even been made. Every caller was bounced to the portfolio, which is precisely
 * the behaviour this component exists to replace.
 *
 * `shouldFetch` is the honest predicate: it is true while the cache has never
 * been loaded for this org, and false once a fetch has succeeded for it. The
 * error arm is load-bearing too — `fetchFailed` leaves `invalidated` false but
 * never records a `loadedKey`, so a failed load would otherwise wait forever.
 */
export function ProjectRedirect() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation('portfolio')
  const orgId = useAppSelector(selectActiveOrgId)
  const projects = useAppSelector(selectProjectsItems)
  const cache = useAppSelector(selectProjectsCache)
  const error = useAppSelector(selectProjectsError)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchProjects(orgId))
  }, [dispatch, orgId])

  const settled =
    orgId === null || error !== null || (!cache.loading && !shouldFetch(cache, orgId))

  if (!settled) return <LoadingPanel label={t('states.loading')} />

  const first = projects[0]
  return first === undefined ? (
    <Navigate to="/projects" replace />
  ) : (
    <Navigate to={`/project/${first.id}`} replace />
  )
}
